import type { FormListFieldData } from 'antd/lib';
import type { MeetingPoint, MeetingPointsFormData } from '../../types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { App, Divider, Flex, Form, Input, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { CloseOutlined, ExclamationCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';

import { useTrackEvent } from '@/hooks/useTrackEvents';
import themeToken from '@lib/theme/tokens/index';
import { StyledButton } from '@/lib/theme/components/Button';
import { PageWrapper } from '@/components/Shared/Wrapper';
import CommonTable from '@/components/Shared/CommonTable';
import HeaderWrapper from '@/components/Shared/HeaderWrapper';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import MainContainer from '@/components/Shared/MainContainer';
import VideoIcon from '@/components/Icons/VideoIcon';
import CaptableDetailSpace from '@/features/captable/components/Shared/CaptableSpace';
import CommonDetailInfo from '@/features/captable/components/Shared/CommonDetailInfo';
import CommonModal from '@/features/captable/components/Shared/CommonModal';
import { StyledInput } from '@/lib/theme/components/Input';
import { StyledSelect } from '@/lib/theme/components/Select';

import { useGetMeetingTypes } from '../../api/meetings/getMeetingTypes';
import { useGetMeetingDetails } from '../../api/meetings/getMeetingDetails';
import { meetingAttendeesColumns } from '../../utils/meetingAttendeesColumns';
import { MeetingStatus } from '../../utils/enums';
import { useDeleteMeeting } from '../../api/meetings/deleteMeeting';
import { useUpdateMeetingStatus } from '../../api/meetings/updateMeetingStatus';
import MeetingForm from '../../components/MeetingForm';
import { useAddMeetingPoint } from '../../api/meetings/addMeetingPoint';
import { useGetMeetingAttendees } from '../../api/meetings/getMeetingAttendees';
import { useDeleteMeetingPoint } from '../../api/meetings/deleteMeetingPoint';
import { useEditMeetingPoint } from '../../api/meetings/editMeetingPoint';
import DelegateForm from '../../components/DelegateForm';
import { myVotesColumns } from '../../utils/myVotesColumns';
import { useGetMyVotes } from '../../api/meetings/getMyVotes';
import { normalizeHtml, stripTemporaryTag } from '@/utils/htmlConvert';
import SharedModal from '@/components/Shared/SharedModal';
// import Quill, { Delta } from 'quill';
import { usePermissionsStore } from '@/stores/Permissions';
import { useEditMeeting } from '../../api/meetings/editMeeting';
// import { useGetVotingPoints } from '../../api/meetings/getVotingPoints';
import JoditEditor from 'jodit-react';
import { useGetMeetingAttendeesWithShares } from '@/features/vote/api/getMeetingAttendeesWithShares';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MeetingDetails = () => {
  const { sendMixpanelEvent } = useTrackEvent({ page: 'meeting-details' });

  const { id: meetingID } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { permissions } = usePermissionsStore((state) => state);

  const { t } = useTranslation('investorRelations', {
    keyPrefix: 'meetings',
  });

  const { t: translate } = useTranslation('pageTitles', {
    keyPrefix: 'investorRelations',
  });

  useDocumentTitle(translate('meetingDetails'));

  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { notification: toaster } = App.useApp();

  // const [, setEditorValue] = useState<Delta>();

  const [form] = Form.useForm();
  const voting_points = Form.useWatch<MeetingPointsFormData[]>('voting_points', form);
  const [attendeesPaginationParams, setAttendeesPaginationParams] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const [attendeesOptionsPaginationParams, setAttendeesOptionsPaginationParams] = useState({
    pageSize: 10,
    current: 1,
    total: 0,
  });
  const [attendeesOptions, setAttendeesOptions] = useState<{ value: number; label: string }[]>([]);
  const {
    current: attendeesOptionsCurrentPage,
    pageSize: attendeesOptionsPageSize,
    total: attendeesOptionsTotal,
  } = attendeesOptionsPaginationParams;

  const { data: meetingDetails, isLoading: isGetMeetingDetailsLoading } = useGetMeetingDetails({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
  });

  const { data: meetingAttendees, isLoading: isGetMeetingAttendeesLoading } = useGetMeetingAttendees({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
    queryParams: {
      page: attendeesPaginationParams.current,
      limit: attendeesPaginationParams.pageSize,
    },
  });

  const {
    data: meetingAttendeesWithShares,
    dataUpdatedAt: meetingAttendeesWithSharesUpdatedAt,
    error: meetingAttendanceReportError,
    isFailed: isMeetingAttendanceReportFailed,
    isGenerating: isMeetingAttendanceReportGenerating,
    generate: generateMeetingAttendanceReport,
  } = useGetMeetingAttendeesWithShares({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : undefined,
  });

  const { data: meetingAttendeesOptions, isFetching: isGetMeetingAttendeesOptionsFetching } = useGetMeetingAttendees({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
    queryParams: {
      page: attendeesOptionsCurrentPage,
      limit: attendeesOptionsPageSize,
    },
  });

  const { data: myVotes, isLoading: isGetMyVotesLoading } = useGetMyVotes({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
  });

  const canCreateMeeting = useMemo(() => permissions.includes('company.board.MOM'), [permissions]);
  const canDeleteMeeting = useMemo(() => permissions.includes('company.board.MOM'), [permissions]);

  const myVotesTableColumns = myVotesColumns(isGetMyVotesLoading, t);

  const editor = useRef(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const downloadedAttendanceReportAtRef = useRef(0);
  const notifiedAttendanceReportErrorRef = useRef<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);

  const config = useMemo(
    () => ({
      readonly: false,
    }),
    [],
  );

  // const { data: votingPoints, isLoading: isGetVotingPointsLoading } = useGetVotingPoints({
  //   companyID: selectedCompany?.cid as number,
  //   meetingID: meetingID ? +meetingID : -1,
  // });

  const { data: meetingTypes, isLoading: isGetMeetingTypesLoading } = useGetMeetingTypes({
    companyID: selectedCompany?.cid as number,
  });

  const { mutate: mutateDeleteMeeting, isPending: isDeleteMeetingPending } = useDeleteMeeting({
    postSuccess: () => navigate('/investor-relations/meetings'),
  });

  const { mutate: mutateUpdateMeetingStatus, isPending: isUpdateMeetingStatusPending } = useUpdateMeetingStatus({
    postSuccess: () => {
      setEditorContent('');
      setPendingAttachments([]);
    },
  });

  const { mutate: mutateEditMeeting, isPending: isEditMeetingPending } = useEditMeeting({});

  const { mutate: mutateAddMeetingPoint, isPending: isAddMeetingPointPending } = useAddMeetingPoint({});
  const { mutate: mutateEditMeetingPoint, isPending: isEditMeetingPointPending } = useEditMeetingPoint({});
  const { mutate: mutateDeleteMeetingPoint, isPending: isDeleteMeetingPointPending } = useDeleteMeetingPoint({});

  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState<boolean>(false);
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);

  const meetingType = useMemo(() => {
    return meetingTypes?.data?.find((type) => type.id === meetingDetails?.data.meeting_type_id);
  }, [meetingDetails, meetingTypes]);

  const meetingAttendeesTableColumns = useMemo(
    () => meetingAttendeesColumns(meetingDetails?.data.status, isGetMeetingDetailsLoading, t),
    [meetingDetails?.data.status, isGetMeetingDetailsLoading, t],
  );

  const isHeaderBtnsVisible = useMemo(() => {
    if (
      !canCreateMeeting ||
      [MeetingStatus.FINISHED, MeetingStatus.CANCELED].includes(meetingDetails?.data?.status as MeetingStatus)
    ) {
      return false;
    }
    return true;
  }, [meetingDetails?.data?.status, canCreateMeeting]);

  const handleMeetingModalOpen = useCallback(() => {
    sendMixpanelEvent('edit-meeting-btn-clicked');

    setIsMeetingModalOpen(true);
  }, [setIsMeetingModalOpen, sendMixpanelEvent]);

  const handleMeetingModalCancel = useCallback(() => {
    setIsMeetingModalOpen(false);
  }, [setIsMeetingModalOpen]);

  const handleDelegateModalOpen = useCallback(() => {
    sendMixpanelEvent('delegate-voting-btn-clicked');

    setIsDelegateModalOpen(true);
  }, [setIsDelegateModalOpen, sendMixpanelEvent]);

  const handleDelegateModalCancel = useCallback(() => {
    setIsDelegateModalOpen(false);
  }, [setIsDelegateModalOpen]);

  const cancelMeetingHandler = useCallback(
    (selectedMeetingID: number, meetingStatus: MeetingStatus) => {
      if (meetingStatus === MeetingStatus.DRAFTED) {
        mutateDeleteMeeting({ companyID: selectedCompany?.cid as number, meetingID: selectedMeetingID });
      } else {
        const body = {
          status: MeetingStatus.CANCELED,
        };

        mutateUpdateMeetingStatus({ companyID: selectedCompany?.cid as number, meetingID: selectedMeetingID, body });
      }
    },
    [selectedCompany?.cid, mutateDeleteMeeting, mutateUpdateMeetingStatus],
  );

  const onVotingPointAdd = useCallback(
    (values: MeetingPointsFormData, selectedPoint: MeetingPoint | undefined) => {
      if (selectedPoint) {
        const excludedUsers: number[] = [];
        if (Array.isArray(values.excluded_users) && values.excluded_users.length > 0) {
          if (typeof values.excluded_users[0] === 'number') {
            excludedUsers.push(...(values.excluded_users as number[]));
          } else {
            (values.excluded_users as { value: number; label?: string }[]).forEach((user) => {
              excludedUsers.push(user.value);
            });
          }
        }
        mutateEditMeetingPoint({
          companyID: selectedCompany?.cid as number,
          meetingID: meetingDetails?.data?.id as number,
          meetingPointID: selectedPoint.id,
          body: {
            points_description: values.description,
            clause: values.clause,
            excluded_users: excludedUsers,
          },
        });
      } else {
        mutateAddMeetingPoint({
          companyID: selectedCompany?.cid as number,
          meetingID: meetingDetails?.data?.id as number,
          body: {
            clause: values.clause,
            points_description: values.description,
            excluded_users: (values.excluded_users || []) as number[],
          },
        });
      }
    },
    [selectedCompany?.cid, meetingDetails?.data?.id, mutateAddMeetingPoint, mutateEditMeetingPoint],
  );

  const extractEmbeddedFilesFromHtml = useCallback(async (html: string) => {
    const dataUrlPattern = /(src|href)=(['"])(data:[^'"]+)\2/g;
    const matches = [...html.matchAll(dataUrlPattern)];
    const files: File[] = [];

    for (let i = 0; i < matches.length; i += 1) {
      const dataUrl = matches[i][3];
      if (!dataUrl?.startsWith('data:')) continue;

      const blob = await fetch(dataUrl).then((response) => response.blob());
      const extension = blob.type.split('/')[1] || 'bin';
      files.push(
        new File([blob], `meeting-attachment-${Date.now()}-${i + 1}.${extension}`, {
          type: blob.type,
        }),
      );
    }

    return files;
  }, []);

  const getFileKey = useCallback((file: File) => `${file.name}-${file.size}-${file.lastModified}`, []);

  const handleAttachmentSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      setPendingAttachments((prev) => {
        const existing = new Set(prev.map(getFileKey));
        const uniqueFiles = files.filter((file) => !existing.has(getFileKey(file)));
        return [...prev, ...uniqueFiles];
      });

      event.target.value = '';
    },
    [getFileKey],
  );

  const handleMeetingSave = useCallback(async () => {
    const body = new FormData();
    const embeddedAttachments = await extractEmbeddedFilesFromHtml(editorContent || '');
    const allAttachmentsMap = new Map<string, File>();

    [...pendingAttachments, ...embeddedAttachments].forEach((file) => {
      allAttachmentsMap.set(getFileKey(file), file);
    });

    if (meetingDetails?.data.status === MeetingStatus.DRAFTED) {
      body.append('announcement', editorContent || '');
      body.append('status', `${MeetingStatus.ANNOUNCED}`);
    } else if (meetingDetails?.data.status === MeetingStatus.ANNOUNCED) {
      body.append('minute_of_meeting', editorContent || '');
      body.append('status', `${MeetingStatus.FINISHED}`);
    }

    [...allAttachmentsMap.values()].forEach((file) => {
      body.append('attachments', file, encodeURI(file.name));
    });

    mutateUpdateMeetingStatus({
      companyID: selectedCompany?.cid as number,
      meetingID: meetingDetails?.data?.id as number,
      body,
    });
  }, [
    selectedCompany?.cid,
    meetingDetails?.data,
    mutateUpdateMeetingStatus,
    editorContent,
    extractEmbeddedFilesFromHtml,
    pendingAttachments,
    getFileKey,
  ]);

  const handleVotingPointRemove = useCallback(
    (field: FormListFieldData, onRemove: (index: number) => void) => {
      const selectedPoint = meetingDetails?.data?.meeting_points?.[field.key];
      if (selectedPoint) {
        mutateDeleteMeetingPoint(
          {
            companyID: selectedCompany?.cid as number,
            meetingID: meetingDetails?.data?.id,
            meetingPointID: selectedPoint.id,
          },
          {
            onSuccess: () => onRemove?.(field.name),
          },
        );
      } else {
        onRemove?.(field.name);
      }
    },
    [selectedCompany?.cid, meetingDetails?.data, mutateDeleteMeetingPoint],
  );

  const canLoadMoreAttendeesOptions = useMemo(
    () => attendeesOptionsCurrentPage * attendeesOptionsPageSize < attendeesOptionsTotal,
    [attendeesOptionsCurrentPage, attendeesOptionsPageSize, attendeesOptionsTotal],
  );

  const handleAttendeesOptionsPopupScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 24;
      if (isNearBottom && canLoadMoreAttendeesOptions && !isGetMeetingAttendeesOptionsFetching) {
        setAttendeesOptionsPaginationParams((prev) => ({ ...prev, current: prev.current + 1 }));
      }
    },
    [canLoadMoreAttendeesOptions, isGetMeetingAttendeesOptionsFetching],
  );

  const editorDefaultValueSetter = useCallback(() => {
    if (meetingDetails?.data?.announcement && meetingDetails?.data?.status === MeetingStatus.DRAFTED) {
      setEditorContent(meetingDetails?.data?.announcement);
    }
    if (meetingDetails?.data?.minutes_of_meeting && meetingDetails?.data?.status === MeetingStatus.ANNOUNCED) {
      setEditorContent(meetingDetails?.data?.minutes_of_meeting);
    }
  }, [meetingDetails?.data?.announcement, meetingDetails?.data?.minutes_of_meeting, meetingDetails?.data?.status]);

  useEffect(() => {
    editorDefaultValueSetter();
    if (meetingDetails?.data?.meeting_points?.length) {
      form.setFieldsValue({
        voting_points: meetingDetails?.data?.meeting_points?.map((point) => ({
          clause: point.clause,
          description: point.points_description,
          excluded_users: point.meeting_point_excluded_users.map((user) => {
            return {
              value: user.meeting_attendee.id,
              label: user.meeting_attendee.company_user.name,
            };
          }),
        })),
      });
    }
  }, [form, meetingDetails?.data, editorDefaultValueSetter]);

  useEffect(() => {
    if (
      !meetingAttendeesWithShares ||
      downloadedAttendanceReportAtRef.current === meetingAttendeesWithSharesUpdatedAt
    ) {
      return;
    }

    downloadedAttendanceReportAtRef.current = meetingAttendeesWithSharesUpdatedAt;
    const link = document.createElement('a');
    link.href = meetingAttendeesWithShares.download_url;
    link.setAttribute('target', '_blank');
    link.setAttribute('download', meetingAttendeesWithShares.file_name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [meetingAttendeesWithShares, meetingAttendeesWithSharesUpdatedAt]);

  useEffect(() => {
    if (isMeetingAttendanceReportGenerating) {
      notifiedAttendanceReportErrorRef.current = null;
      return;
    }

    if (
      isMeetingAttendanceReportFailed &&
      meetingAttendanceReportError &&
      notifiedAttendanceReportErrorRef.current !== meetingAttendanceReportError
    ) {
      notifiedAttendanceReportErrorRef.current = meetingAttendanceReportError;
      toaster.error({
        message: t('attendanceReportFailed'),
        description: meetingAttendanceReportError,
      });
    }
  }, [isMeetingAttendanceReportFailed, isMeetingAttendanceReportGenerating, meetingAttendanceReportError, t, toaster]);

  useEffect(() => {
    if (meetingAttendees?.metadata) {
      const { total } = meetingAttendees.metadata;
      setAttendeesPaginationParams((prev) => ({ ...prev, total }));
    }
  }, [meetingAttendees?.metadata]);

  useEffect(() => {
    setAttendeesOptions([]);
    setAttendeesOptionsPaginationParams((prev) => ({ ...prev, current: 1, total: 0 }));
  }, [meetingID, selectedCompany?.cid]);

  useEffect(() => {
    if (!meetingAttendeesOptions) return;

    const { metadata, data } = meetingAttendeesOptions;
    setAttendeesOptionsPaginationParams((prev) => ({ ...prev, total: metadata.total }));

    const mappedOptions = data.map((attendee) => ({
      value: attendee.id,
      label: attendee.company_user.name,
    }));

    setAttendeesOptions((prev) => {
      if (attendeesOptionsCurrentPage === 1) {
        return mappedOptions;
      }
      const existingIds = new Set(prev.map((option) => option.value));
      const nextOptions = mappedOptions.filter((option) => !existingIds.has(option.value));
      return [...prev, ...nextOptions];
    });
  }, [meetingAttendeesOptions, attendeesOptionsCurrentPage]);

  const notificationMessage = () => {
    if (meetingDetails?.data.status === MeetingStatus.DRAFTED) {
      return (
        <Flex
          gap={10}
          style={{
            backgroundColor: '#FFF5EC',
            border: '1px solid #FDE1C4',
            padding: '12px',
          }}
        >
          <ExclamationCircleOutlined
            style={{
              color: themeToken.colorWarningText,
            }}
          />
          <Text
            style={{
              color: themeToken.colorTextSecondary,
            }}
          >
            {t('announcementNotification')}
          </Text>
        </Flex>
      );
    } else if (meetingDetails?.data.status === MeetingStatus.ANNOUNCED) {
      return (
        <Flex
          gap={10}
          style={{
            backgroundColor: '#FFF5EC',
            border: '1px solid #FDE1C4',
            padding: '12px',
          }}
        >
          <ExclamationCircleOutlined
            style={{
              color: themeToken.colorWarningText,
            }}
          />
          <Text
            style={{
              color: themeToken.colorTextSecondary,
            }}
          >
            {t('meetingMinutesDescription')}
          </Text>
        </Flex>
      );
    } else {
      return null;
    }
  };

  const saveAnnouncmentOrMinutesOfMeetings = () => {
    if (meetingDetails?.data.status === MeetingStatus.DRAFTED) {
      console.log('saving announcment', editorContent);
      mutateEditMeeting({
        companyID: selectedCompany?.cid as number,
        meetingID: meetingDetails?.data?.id,
        body: {
          announcement: editorContent || '',
        },
      });
    } else if (meetingDetails?.data.status === MeetingStatus.ANNOUNCED) {
      mutateEditMeeting({
        companyID: selectedCompany?.cid as number,
        meetingID: meetingDetails?.data?.id,
        body: {
          minutes_of_meeting: editorContent || '',
        },
      });
    }
  };

  const renderMinuteOfMeeting = () => {
    const raw = meetingDetails?.data?.minutes_of_meeting;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const normalized = normalizeHtml(raw);
    const cleanedTemp = stripTemporaryTag(`${normalized}`);
    return <div dangerouslySetInnerHTML={{ __html: cleanedTemp }} />;
  };

  return (
    <MainContainer
      title={t('investorRelations')}
      breadcrumbItems={[
        {
          href: '/investor-relations/meetings',
          title: (
            <>
              <VideoIcon width={12} height={20} active />
              {t('meetings')}
            </>
          ),
        },
        {
          title: t('meetingDetails'),
        },
      ]}
    >
      <Flex vertical gap={16}>
        <PageWrapper
          style={{
            minHeight: 'auto',
          }}
        >
          <HeaderWrapper>
            <Title
              level={5}
              style={{
                color: themeToken['branding-primary-6'],
                fontWeight: 600,
                marginBottom: 0,
              }}
            >
              {t('meetingDetails')}
            </Title>

            <Flex gap={16} align="center">
              {isHeaderBtnsVisible && (
                <StyledButton
                  type="primary"
                  onClick={handleMeetingModalOpen}
                  disabled={isGetMeetingDetailsLoading || isGetMeetingTypesLoading || !canCreateMeeting}
                >
                  {t('editInfo')}
                </StyledButton>
              )}
              {canCreateMeeting && (
                <>
                  <StyledButton
                    type="primary"
                    ghost
                    onClick={() => {
                      navigate(`/vote/resultsByShareholder/${meetingDetails?.data?.id}`);
                    }}
                    disabled={isGetMeetingDetailsLoading || isGetMeetingTypesLoading}
                  >
                    {t('resultsByShareholder')}
                  </StyledButton>
                  <StyledButton
                    type="primary"
                    ghost
                    onClick={() => {
                      navigate(`/vote/results/${meetingDetails?.data?.id}`);
                    }}
                    disabled={isGetMeetingDetailsLoading || isGetMeetingTypesLoading}
                  >
                    {t('results')}
                  </StyledButton>
                  <StyledButton
                    type="primary"
                    ghost
                    loading={isMeetingAttendanceReportGenerating}
                    disabled={
                      isGetMeetingDetailsLoading || isGetMeetingTypesLoading || isMeetingAttendanceReportGenerating
                    }
                    onClick={generateMeetingAttendanceReport}
                  >
                    {isMeetingAttendanceReportGenerating ? t('generatingAttendanceReport') : t('attendanceReport')}
                  </StyledButton>
                </>
              )}
              {isHeaderBtnsVisible && (
                <StyledButton
                  type="primary"
                  ghost
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={
                    isGetMeetingDetailsLoading ||
                    isGetMeetingTypesLoading ||
                    isDeleteMeetingPending ||
                    isUpdateMeetingStatusPending ||
                    !canDeleteMeeting
                  }
                >
                  {meetingDetails?.data?.status === MeetingStatus.DRAFTED ? t('deleteDraft') : t('cancelMeeting')}
                </StyledButton>
              )}
            </Flex>
          </HeaderWrapper>
          <Divider
            style={{
              marginBlock: themeToken.margin,
              borderColor: themeToken['branding-natural-3'],
            }}
          />
          <CaptableDetailSpace>
            <CommonDetailInfo title={t('name')} value={meetingDetails?.data.name} vertical />
            <CommonDetailInfo
              title={t('dateAndTime')}
              value={dayjs(meetingDetails?.data.start_time).format('MMM D, YYYY, h:mm A')}
              vertical
            />
            <CommonDetailInfo title={t('type')} value={meetingType?.name} vertical />
            <CommonDetailInfo title={t('location')} value={meetingDetails?.data.location || '-'} vertical />
            <CommonDetailInfo
              title={t('meetingLink')}
              value={
                meetingDetails?.data.meeting_link ? (
                  <a style={{ textDecoration: 'underline' }} href={meetingDetails?.data.meeting_link}>
                    {t('link')}
                  </a>
                ) : (
                  '-'
                )
              }
              vertical
            />
          </CaptableDetailSpace>
        </PageWrapper>

        {canCreateMeeting && (
          <PageWrapper
            style={{
              minHeight: 'auto',
            }}
          >
            <HeaderWrapper>
              <Title
                level={5}
                style={{
                  color: themeToken['branding-primary-6'],
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                {t('meetingAttendees')}
              </Title>
            </HeaderWrapper>
            <CommonTable
              columns={meetingAttendeesTableColumns}
              dataSource={meetingAttendees?.data}
              isLoading={isGetMeetingAttendeesLoading}
              paginationParams={attendeesPaginationParams}
              setPaginationParams={setAttendeesPaginationParams}
            />
          </PageWrapper>
        )}

        <PageWrapper
          style={{
            minHeight: 'auto',
          }}
        >
          <HeaderWrapper>
            <Flex gap={4} vertical flex={1}>
              <Flex justify="space-between" align="center" flex={1}>
                {meetingDetails?.data.status === MeetingStatus.DRAFTED ? (
                  <Title
                    level={5}
                    style={{
                      color: themeToken['branding-primary-6'],
                      fontWeight: 600,
                      marginBottom: 0,
                    }}
                  >
                    {t('votingPoints')}{' '}
                  </Title>
                ) : (
                  <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                    <Title
                      level={5}
                      style={{
                        color: themeToken['branding-primary-6'],
                        fontWeight: 600,
                        marginBottom: 0,
                      }}
                    >
                      {t('discussedPoints')}
                    </Title>
                    {meetingDetails?.data.status !== MeetingStatus.FINISHED && (
                      <Flex gap={8}>
                        <StyledButton
                          type="primary"
                          disabled={meetingDetails?.data.isUserDelegated}
                          size="middle"
                          onClick={() => navigate(`/vote/questions/${meetingDetails?.data?.id}`)}
                        >
                          {t('vote')}
                        </StyledButton>
                        <StyledButton
                          disabled={meetingDetails?.data.isUserDelegated}
                          type="primary"
                          ghost
                          onClick={handleDelegateModalOpen}
                        >
                          {t('sendDelegate')}
                        </StyledButton>
                      </Flex>
                    )}
                  </Flex>
                )}
              </Flex>
              <Text
                style={{
                  color: '#00000073',
                  fontSize: '12px',
                }}
              >
                {meetingDetails?.data.status === MeetingStatus.DRAFTED && t('votingPointsSubTitle')}
              </Text>
            </Flex>
          </HeaderWrapper>
          {meetingDetails?.data.status === MeetingStatus.DRAFTED ? (
            <Form
              name="voting_points"
              colon={false}
              form={form}
              disabled={
                // isGetVotingPointsLoading ||
                isGetMeetingDetailsLoading ||
                isAddMeetingPointPending ||
                isDeleteMeetingPointPending ||
                isEditMeetingPointPending
              }
            >
              <Form.List name="voting_points">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Flex key={field.key} align="center" justify="space-between">
                        <Form.Item
                          label={`${index + 1}.`}
                          required={false}
                          style={{
                            width: '100%',
                          }}
                        >
                          <Flex align="center" justify="space-between" gap={8}>
                            <Form.Item
                              {...field}
                              key={`clause-${index}`}
                              name={[field.name, 'clause']}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Please input point clause or delete this field.',
                                },
                              ]}
                              noStyle
                            >
                              <StyledInput
                                placeholder={t('votingPointClausePlaceholder')}
                                style={{
                                  width: '25%',
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`description-${index}`}
                              name={[field.name, 'description']}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                {
                                  required: true,
                                  whitespace: true,
                                  message: 'Please input point description or delete this field.',
                                },
                              ]}
                              noStyle
                            >
                              <StyledInput placeholder={t('votingPointDescriptionPlaceholder')} />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`excluded_users-${index}`}
                              name={[field.name, 'excluded_users']}
                              rules={[
                                { required: true, message: 'Please select excluded users or leave empty for none' },
                              ]}
                              noStyle
                            >
                              <StyledSelect
                                loading={isGetMeetingAttendeesOptionsFetching}
                                options={attendeesOptions}
                                placeholder={t('selectExcludedUsers')}
                                mode="multiple"
                                showSearch
                                onPopupScroll={handleAttendeesOptionsPopupScroll}
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '')
                                    .toLowerCase()
                                    .localeCompare((optionB?.label ?? '').toLowerCase())
                                }
                                dropdownRender={(menu) => (
                                  <>
                                    {menu}
                                    {isGetMeetingAttendeesOptionsFetching && (
                                      <Flex justify="center" style={{ padding: '8px 0' }}>
                                        <Spin size="small" />
                                      </Flex>
                                    )}
                                  </>
                                )}
                              />
                            </Form.Item>
                            <Flex gap={8}>
                              <StyledButton
                                type="text"
                                onClick={() =>
                                  onVotingPointAdd(
                                    voting_points[index],
                                    meetingDetails?.data?.meeting_points?.[field.key],
                                  )
                                }
                                style={{
                                  color: themeToken.colorPrimary,
                                  height: '24px',
                                  padding: '4px',
                                }}
                                icon={<PlusCircleOutlined />}
                              >
                                {meetingDetails?.data?.meeting_points?.[field.key] ? t('update') : t('add')}
                              </StyledButton>
                              {fields.length >= 1 && (
                                <StyledButton
                                  type="text"
                                  onClick={() => handleVotingPointRemove(field, remove)}
                                  style={{
                                    color: themeToken.red,
                                    height: '24px',
                                    padding: '4px',
                                  }}
                                  icon={<CloseOutlined />}
                                >
                                  {t('remove')}
                                </StyledButton>
                              )}
                            </Flex>
                          </Flex>
                        </Form.Item>
                      </Flex>
                    ))}
                    <Form.Item label={`${(voting_points?.length ?? 0) + 1}.`}>
                      <StyledButton
                        type="text"
                        onClick={() => add()}
                        style={{
                          color: themeToken.colorPrimary,
                          height: '24px',
                          padding: '4px',
                        }}
                        icon={<PlusCircleOutlined />}
                      >
                        {`${t('addNewPoint')}`}
                      </StyledButton>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          ) : (
            // meetingDetails?.data?.meeting_points?.map((meetingPoint, idx) => (
            //   <MeetingPointContainer
            //     key={meetingPoint.id}
            //     pointIndex={idx}
            //     meetingId={meetingID}
            //     meetingPoint={meetingPoint}
            //   />
            // ))
            <CommonTable
              columns={myVotesTableColumns}
              dataSource={myVotes?.data}
              isLoading={isGetMyVotesLoading}
              pagination={false}
            />
          )}
        </PageWrapper>

        <PageWrapper
          style={{
            minHeight: 'auto',
          }}
        >
          {!canCreateMeeting && (
            <Flex gap={4} vertical>
              <Title
                level={5}
                style={{
                  color: themeToken['branding-primary-6'],
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                {t('meetingMinutes')}
              </Title>
              <Flex
                gap={10}
                style={{
                  backgroundColor: '#FFF5EC',
                  border: '1px solid #FDE1C4',
                  padding: '12px',
                }}
              >
                <ExclamationCircleOutlined
                  style={{
                    color: themeToken.colorWarningText,
                  }}
                />
                <Text
                  style={{
                    color: themeToken.colorTextSecondary,
                  }}
                >
                  {t('meetingMinutesDescription')}
                </Text>
              </Flex>
              {meetingDetails?.data?.status && [MeetingStatus.ANNOUNCED].includes(meetingDetails?.data?.status) && (
                <Flex
                  gap={16}
                  style={{
                    marginTop: 12,
                  }}
                  vertical
                >
                  <TextArea rows={4} disabled />
                </Flex>
              )}
            </Flex>
          )}
          {canCreateMeeting && (
            <Flex gap={4} vertical>
              <Title
                level={5}
                style={{
                  color: themeToken['branding-primary-6'],
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                {meetingDetails?.data.status === MeetingStatus.DRAFTED ? t('announcement') : t('meetingMinutes')}
              </Title>
              {notificationMessage()}
              {meetingDetails?.data?.status &&
                [MeetingStatus.DRAFTED, MeetingStatus.ANNOUNCED, MeetingStatus.FINISHED].includes(
                  meetingDetails?.data?.status,
                ) && (
                  <Flex
                    gap={16}
                    style={{
                      marginTop: 12,
                    }}
                    vertical
                  >
                    {meetingDetails?.data.status !== MeetingStatus.FINISHED ? (
                      <Flex vertical gap={8} style={{ width: '100%' }}>
                        <JoditEditor
                          ref={editor}
                          value={editorContent}
                          config={config}
                          tabIndex={1} // tabIndex of textarea
                          onBlur={(newContent) => {
                            setEditorContent(newContent);
                          }} // preferred to use only this option to update the content for performance reasons
                        />
                        <input
                          ref={attachmentInputRef}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handleAttachmentSelection}
                        />
                        <Flex gap={8} align="center" justify="space-between">
                          <StyledButton
                            type="default"
                            onClick={() => attachmentInputRef.current?.click()}
                            disabled={isUpdateMeetingStatusPending}
                          >
                            {t('attachments')}
                          </StyledButton>
                          <Text
                            style={{
                              color: themeToken.colorTextSecondary,
                            }}
                          >
                            {pendingAttachments.length > 0
                              ? `${pendingAttachments.length} file(s) ready to send`
                              : 'No attachments selected'}
                          </Text>
                        </Flex>
                        <StyledButton
                          type="primary"
                          style={{
                            width: 'fit-content',
                            alignSelf: 'end',
                          }}
                          disabled={isEditMeetingPending || isUpdateMeetingStatusPending}
                          onClick={() => void saveAnnouncmentOrMinutesOfMeetings()}
                        >
                          {meetingDetails?.data.status === MeetingStatus.DRAFTED
                            ? t('saveAnnouncementDraft')
                            : t('saveMinutesDraft')}
                        </StyledButton>
                      </Flex>
                    ) : (
                      <div>{renderMinuteOfMeeting()}</div>
                    )}
                    {meetingDetails?.data.status !== MeetingStatus.FINISHED ? (
                      <Flex gap={16} justify="end">
                        <StyledButton type="primary" onClick={() => void handleMeetingSave()}>
                          {meetingDetails?.data.status === MeetingStatus.DRAFTED
                            ? t('saveAndSend')
                            : t('finishMeeting')}
                        </StyledButton>
                      </Flex>
                    ) : null}
                  </Flex>
                )}
            </Flex>
          )}
        </PageWrapper>
      </Flex>
      <CommonModal isModalOpen={isMeetingModalOpen} handleCancel={handleMeetingModalCancel} destroyOnClose>
        <MeetingForm
          handleCancel={handleMeetingModalCancel}
          handleOk={handleMeetingModalCancel}
          selectedMeetingId={meetingID ? +meetingID : undefined}
        />
      </CommonModal>
      <CommonModal isModalOpen={isDelegateModalOpen} handleCancel={handleDelegateModalCancel} destroyOnClose>
        <DelegateForm
          handleCancel={handleDelegateModalCancel}
          handleOk={handleDelegateModalCancel}
          selectedMeetingId={meetingID ? +meetingID : undefined}
        />
      </CommonModal>
      <SharedModal
        open={confirmDeleteOpen}
        setConfirmDeleteOpen={setConfirmDeleteOpen}
        onOk={() =>
          cancelMeetingHandler(meetingDetails?.data?.id as number, meetingDetails?.data?.status as MeetingStatus)
        }
        confirmationText={t('yesDelete')}
        title={t('deleteWarning')}
        content={
          <Text>
            {t('deleteConfirmation')} <span style={{ fontWeight: 'bold' }}> {meetingDetails?.data?.name} </span> ?{' '}
          </Text>
        }
      />
    </MainContainer>
  );
};

export default MeetingDetails;
